import { z } from 'zod';
import { TicketStatus } from './enums.js';
import { PRODUCT_STATUSES, ENGINEERING_STATUSES, DEVOPS_STATUSES } from './ticket-departments.js';
export const STATUS_DEPARTMENTS = ['product', 'support', 'engineering', 'devops'];
export const StatusVisibility = z.enum(['hidden', 'when_used', 'always']);
export const TicketStatusDefinition = z.object({
    id: TicketStatus,
    label: z.string().trim().min(1).max(80),
    departments: z.object({ product: StatusVisibility, support: StatusVisibility, engineering: StatusVisibility, devops: StatusVisibility }).strict(),
}).strict();
export const TicketStatusConfig = z.object({ statuses: z.array(TicketStatusDefinition).length(TicketStatus.options.length) }).strict().superRefine((config, ctx) => {
    const ids = new Set(config.statuses.map(s => s.id));
    if (ids.size !== TicketStatus.options.length)
        ctx.addIssue({ code: 'custom', message: 'Include each existing status exactly once.', path: ['statuses'] });
    const names = config.statuses.map(s => s.label.toLocaleLowerCase());
    if (new Set(names).size !== names.length)
        ctx.addIssue({ code: 'custom', message: 'Status names must be unique.', path: ['statuses'] });
});
export const DEFAULT_STATUS_NAMES = {
    draft: 'Draft', submitted: 'Submitted', triage: 'Triage', backlog: 'Backlog (New)',
    design_needed: 'Design Needed', acceptance_criteria_needed: 'Acceptance Criteria Needed', estimate_needed: 'Estimate Needed', needs_approval: 'Needs Approval',
    approved: 'Approved', approved_for_agent: 'Approved · Agent', in_progress: 'In Progress', agent_in_progress: 'Agent Working', internal_waiting: 'Internal Waiting',
    ready_for_qa: 'Ready for QA', qa: 'QA In Progress', dev_done: 'Dev Done', ready_for_uat: 'Ready for UAT', uat: 'UAT', staging: 'Staging', done: 'Done', cancelled: 'Cancelled', rejected: 'Rejected',
};
const order = [...new Set([...PRODUCT_STATUSES, ...ENGINEERING_STATUSES, 'triage', 'staging', ...TicketStatus.options])];
export const DEFAULT_TICKET_STATUS_CONFIG = {
    statuses: order.map(id => ({ id, label: DEFAULT_STATUS_NAMES[id], departments: {
            product: PRODUCT_STATUSES.includes(id) ? 'always' : ['draft', 'submitted', 'triage', 'approved_for_agent'].includes(id) ? 'when_used' : 'hidden',
            engineering: ENGINEERING_STATUSES.includes(id) ? 'always' : ['approved_for_agent', 'agent_in_progress', 'internal_waiting', 'staging'].includes(id) ? 'when_used' : 'hidden',
            support: ENGINEERING_STATUSES.includes(id) ? 'always' : 'when_used',
            devops: DEVOPS_STATUSES.includes(id) ? 'always' : 'when_used',
        } })),
};
/** Department membership is a view filter, never a transition permission. */
export function departmentAql(config, department) {
    if (department === 'all')
        return '';
    const types = department === 'devops' ? 'type = infrastructure' : department === 'support' ? 'type IN (customer_support, internal_bug)' : 'type IN (feature_request, change_request, task, tech_debt, epic, subtask)';
    const statuses = config.statuses.filter(s => s.departments[department] !== 'hidden').map(s => s.id);
    // Preserve legacy/custom states in the existing type-only Support/DevOps views.
    if (statuses.length === TicketStatus.options.length)
        return types;
    return `${types} AND ${statuses.length ? `status IN (${statuses.join(', ')})` : 'status = done AND status != done'}`;
}
//# sourceMappingURL=ticket-status-config.js.map